import React, { useState, useEffect } from 'react';
import './App.css';
import AppAnalytics from './mainComponent';
import LoginForm from './loginScreen';
import { CircularProgress, Box, Button, Container, Typography, Grid, Card, CardActionArea, CardContent, Stack, Avatar } from '@mui/material';
import { InsightsOutlined, GroupOutlined, KeyboardArrowRight } from '@mui/icons-material';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginFailed, setLoginFailed] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('view_mode') || '';
  });

  // Check auth on every page load
  useEffect(() => {
    const savedUsername = localStorage.getItem(process.env.REACT_APP_AUTH_STORAGE_KEY);
    const savedPassword = localStorage.getItem(process.env.REACT_APP_AUTH_PASSWORD_KEY);
    if (savedUsername && savedPassword) { 
      setLoginFailed(false);
      setIsAuthenticated(true);
    }
    setInitializing(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    const headers = new Headers();
    headers.set('Authorization', 'Basic ' + btoa(username + ':' + password));

    try {
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_API_SECURE_DATA_ENDPOINT}`;
      const res = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
      });

      if (res.ok) {
        setIsAuthenticated(true);
        localStorage.setItem(process.env.REACT_APP_AUTH_STORAGE_KEY, username);
        localStorage.setItem(process.env.REACT_APP_AUTH_PASSWORD_KEY, password);
        setLoginFailed(false);
      } else {
        setLoginFailed(true);
      }
    } catch (error) {
      console.error('Error:', error);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error connecting to server');
      }
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    localStorage.removeItem(process.env.REACT_APP_AUTH_STORAGE_KEY);
    localStorage.removeItem(process.env.REACT_APP_AUTH_PASSWORD_KEY);
    setViewMode('');
    localStorage.removeItem('view_mode');
  };

  const handleSelectMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('view_mode', mode);
  };

  if(initializing){
    return (<div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      width: '100%',
    }}>
      <CircularProgress />
    </div>)
  };

  const menuOptions = [
    {
      key: 'subscriptions',
      title: 'Apps Subscriptions Analytics',
      icon: InsightsOutlined,
    },
    {
      key: 'users',
      title: 'Anesthesia Assistant App Users Insights',
      icon: GroupOutlined,
    }
  ];

  return (
    <>
    {!isAuthenticated ? (
      <LoginForm
        username={username}
        password={password}
        setUsername={setUsername}
        setPassword={setPassword}
        handleLogin={handleLogin}
        loginFailed={loginFailed}
      />
    ) : (
      <>
        {!viewMode ? (
          <Box
            sx={(theme) => ({
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(140deg, ${theme.palette.primary.light}15 0%, ${theme.palette.background.default} 55%, ${theme.palette.primary.main}12 100%)`,
            })}
          >
            <Container maxWidth="md" disableGutters>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  boxShadow: (theme) => theme.shadows[6],
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: { xs: 3, md: 5 },
                    pt: { xs: 4, md: 5 },
                    pb: { xs: 3, md: 4 },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Choose your dashboard
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Jump into the analytics workspace that fits what you need right now.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ px: { xs: 3, md: 5 }, pb: { xs: 4, md: 5 } }}>
                  <Grid container spacing={3}>
                    {menuOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <Grid item xs={12} sm={6} key={option.key}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: '100%',
                              borderRadius: 2.5,
                              transition: 'transform 200ms ease, box-shadow 200ms ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: (theme) => theme.shadows[8],
                              },
                            }}
                          >
                            <CardActionArea
                              onClick={() => handleSelectMode(option.key)}
                              sx={{ height: '100%' }}
                            >
                              <CardContent>
                                <Stack direction="row" spacing={3} alignItems="center">
                                  <Avatar
                                    variant="rounded"
                                    sx={(theme) => ({
                                      bgcolor: theme.palette.primary.main,
                                      color: theme.palette.primary.contrastText,
                                      width: 56,
                                      height: 56,
                                    })}
                                  >
                                    <IconComponent fontSize="large" />
                                  </Avatar>
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      {option.title}
                                    </Typography>
                                  </Box>
                                  <KeyboardArrowRight color="action" />
                                </Stack>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
                <Box sx={{ px: { xs: 3, md: 5 }, pb: { xs: 4, md: 5 } }}>
                  <Button variant="text" color="secondary" onClick={handleLogout}>
                    Logout
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>
        ) : (
          <AppAnalytics handleLogout={handleLogout} viewMode={viewMode} handleSelectMode={handleSelectMode}/>
        )}
      </>
    )}
  </>
  );
}

export default App;
